import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Brain, Send, Loader2, Plus, Trash2, AlertTriangle, Sparkles, MessageSquare, Shield,
} from 'lucide-react';
import {
  coachChat, listCoachConversations, getCoachConversation, deleteCoachConversation,
} from '../services/api';
import { InfoTip } from '../components/ui';

const SUGGESTED_PROMPTS = [
  'Look at all my Grid Bots and tell me which ones are underperforming and why.',
  'My DCA Safety bot keeps hitting the last safety order — what should I change?',
  'Based on my 90-day win rate and avg win/loss, what Kelly fraction should I use for new positions?',
  "Which of my strategies is most sensitive to a regime shift from ranging to trending?",
];

export default function StrategyCoachPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateLimit, setRateLimit] = useState(null); // { used, remaining, limit }
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const res = await listCoachConversations();
      setConversations((res?.data || res)?.conversations || []);
    } catch (e) {
      setError(e?.message || 'Failed to load conversations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  async function openConv(convId) {
    try {
      setActiveConvId(convId);
      setError(null);
      const res = await getCoachConversation(convId);
      const data = res?.data || res;
      setMessages(data?.messages || []);
    } catch (e) {
      setError(e?.message || 'Failed to load conversation');
    }
  }

  function newConv() {
    setActiveConvId(null);
    setMessages([]);
    setInput('');
    setError(null);
  }

  async function send(messageOverride) {
    const msg = (messageOverride || input).trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);

    // Optimistic UI
    const userMsg = { role: 'user', content: msg };
    setMessages(m => [...m, userMsg]);
    setInput('');

    try {
      const res = await coachChat({ message: msg, conversationId: activeConvId });
      const data = res?.data || res;
      const newConvId = data?.conversationId;
      const reply = data?.response || '';

      setActiveConvId(newConvId || activeConvId);
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
      if (data?.rateLimit) setRateLimit(data.rateLimit);

      // Refresh sidebar to show new conversation
      loadConvs();
    } catch (e) {
      setError(e?.message || 'Coach failed to respond');
      // Drop the optimistic user message since the request errored
      setMessages(m => m.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
    }
  }

  async function onDeleteConv(convId, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await deleteCoachConversation(convId);
      if (activeConvId === convId) newConv();
      loadConvs();
    } catch (e) {
      setError(e?.message || 'Failed to delete');
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-fuchsia-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-fuchsia-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-fuchsia-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/30 flex items-center justify-center flex-shrink-0">
            <Brain className="w-6 h-6 text-fuchsia-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">AI Strategy Coach</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-400 border border-fuchsia-500/30 font-mono">Elite · Trader's Suite</span>
            </div>
            <p className="text-xs text-fuchsia-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Grounded in your real bot configs + P&L. Strategy-level guidance only — no buy/sell advice, no price predictions.
              <InfoTip side="bottom">Refuses market questions ("should I buy ETH?") by design. Strategy-only ("which of my bots is underperforming?"). Specific questions naming actual bots get the best answers.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4">
        {/* Sidebar */}
        <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-3 max-h-[70vh] overflow-y-auto">
          <button
            onClick={newConv}
            className="w-full mb-2 px-3 py-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/30 text-xs font-medium transition flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> New conversation
          </button>

          {loading ? (
            <div className="text-center py-4 text-gray-400 text-xs"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-[11px] text-gray-500 px-2 py-3">No conversations yet. Ask the coach a question to get started.</div>
          ) : (
            <div className="space-y-1">
              {conversations.map(c => {
                const active = activeConvId === c.conversationId;
                return (
                  <button
                    key={c.conversationId}
                    onClick={() => openConv(c.conversationId)}
                    className={`w-full text-left p-2 rounded-lg transition group ${active ? 'bg-fuchsia-500/15 border border-fuchsia-500/30' : 'hover:bg-dark-700/50 border border-transparent'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-white truncate font-medium">{c.title || 'Untitled'}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{new Date(c.updatedAt).toLocaleString()}</div>
                      </div>
                      <button
                        onClick={(e) => onDeleteConv(c.conversationId, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-500 hover:text-red-400 transition"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Chat panel */}
        <div className="bg-dark-800/60 border border-dark-700 rounded-2xl flex flex-col h-[70vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && !sending && (
              <div className="text-center py-8 space-y-4">
                <MessageSquare className="w-10 h-10 text-fuchsia-400 mx-auto opacity-50" />
                <div className="text-sm text-gray-300">Ask me about your strategies.</div>
                <div className="text-[11px] text-gray-500 max-w-md mx-auto">I see your real bot configs and 90-day P&L. Try one of these:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto px-4">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      className="text-left text-xs px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 text-gray-300 transition"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <Message key={i} role={m.role} content={m.content} />
            ))}

            {sending && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/30 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-fuchsia-400" />
                </div>
                <div className="flex-1 text-sm text-gray-400 flex items-center gap-2 pt-1">
                  <Loader2 className="w-4 h-4 animate-spin" /> Coach is thinking...
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-dark-700 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about your strategies..."
                rows={2}
                className="flex-1 px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm focus:border-fuchsia-500 focus:outline-none resize-none"
                disabled={sending}
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="p-2.5 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/30 transition disabled:opacity-50"
                title="Send (Enter)"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Coach gives strategy-level analysis only. No buy/sell advice. Not financial advice.
              {rateLimit && <span className="ml-2 text-gray-400">· {rateLimit.remaining}/{rateLimit.limit} messages left today</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Message({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-cyan-500/20 border border-cyan-400/30' : 'bg-fuchsia-500/20 border border-fuchsia-400/30'}`}>
        {isUser
          ? <span className="text-[10px] font-bold text-cyan-300">YOU</span>
          : <Brain className="w-4 h-4 text-fuchsia-400" />}
      </div>
      <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${isUser ? 'bg-cyan-500/10 border border-cyan-500/20 text-white' : 'bg-dark-900/60 border border-dark-700 text-gray-200'}`}>
        <MarkdownLite text={content} />
      </div>
    </div>
  );
}

/**
 * Tiny markdown-ish renderer for the chat. Handles **bold**, bullet lines,
 * and paragraph breaks. We deliberately don't pull in a full markdown lib —
 * keeps the surface area small and the output predictable.
 */
function MarkdownLite({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n\n+/);
  return (
    <div className="space-y-2 leading-relaxed">
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const allBullets = lines.every(l => l.trim().startsWith('- ') || l.trim().startsWith('* '));
        if (allBullets && lines.length > 1) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^[-*]\s+/, ''))}</li>)}
            </ul>
          );
        }
        return <p key={i}>{renderInline(block)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
